import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react';

import { bootstrapCounter } from '@/api/counter.api';
import type { Uuid } from '@/api/types';
import { useActivePharmacy } from '@/auth/active-pharmacy';
import { startConnectivityWatch, useOnline } from './connectivity';
import { counterCache, type CounterSnapshot } from './counter-cache';
import { exposeDebugHook, log } from './log';
import { outbox } from './outbox';
import { probeStorage, requestDurability } from './store';
import { drainOutbox, onSyncChange, isSyncing } from './sync';

type OfflineValue = {
  online: boolean;
  /** The shelf and bill series this till is working from. Null until the first load. */
  snapshot: CounterSnapshot | null;
  /** Bills taken but not yet accepted by the server. */
  queued: number;
  /** Bills the server refused for a reason retrying will not fix. */
  failed: number;
  syncing: boolean;
  /** False when the browser cannot store anything durably — see below. */
  durable: boolean;
  /** Why not, in words a pharmacist can act on. Null while it is working. */
  storageReason: string | null;
  refresh: () => Promise<void>;
  sync: () => Promise<void>;
  setSnapshot: (snapshot: CounterSnapshot) => void;
  /** Re-runs the storage probe — the settings screen's "check again". */
  recheckStorage: () => Promise<void>;
};

const OfflineContext = createContext<OfflineValue | null>(null);

/**
 * Keeps the counter working when the line goes down.
 *
 * Three jobs, and they are deliberately in one place because they only make
 * sense together: know whether the server is reachable, hold a snapshot of what
 * the till needs to work without it, and push queued work back the moment it
 * can.
 *
 * The ordering that matters: a reconnect **drains the queue first and only then
 * refreshes the snapshot**. Refreshing first would read a shelf and a bill
 * series that do not yet include the sales sitting in the outbox, and the till
 * would then re-issue numbers it had already printed.
 */
export function OfflineProvider({ children }: { children: ReactNode }) {
  const { pharmacyId } = useActivePharmacy();
  const online = useOnline();

  const [snapshot, setSnapshotState] = useState<CounterSnapshot | null>(null);
  const [queued, setQueued] = useState(0);
  const [failed, setFailed] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [durable, setDurable] = useState(true);
  const [storageReason, setStorageReason] = useState<string | null>(null);

  const recheckStorage = useCallback(async () => {
    const { ok, reason } = await probeStorage();
    setDurable(ok);
    setStorageReason(ok ? null : (reason ?? 'Local storage is unavailable on this device.'));
    if (!ok) log.error('cache', 'storage cannot hold a bill', reason);
  }, []);

  useEffect(() => {
    startConnectivityWatch();
    /*
     * Probed with a real write rather than a bare open. Several browser
     * profiles open the database happily and then refuse every transaction, and
     * discovering that at the moment a bill is taken is too late.
     */
    void recheckStorage();
    // Asks the browser not to evict the queue under disk pressure. Best effort.
    void requestDurability();
  }, [recheckStorage]);

  const recount = useCallback(async () => {
    const pending = await outbox.pending();
    setQueued(pending.filter((entry) => !entry.failed).length);
    setFailed(pending.filter((entry) => entry.failed).length);
  }, []);

  useEffect(() => {
    if (!log.enabled) {
      // Said once, plainly, so nobody has to know the incantation in advance.
      console.info('The Local Pharmacy — run localStorage.setItem("tlp.debug","1") and reload to trace offline behaviour.');
    }
  }, []);

  useEffect(() => {
    const stop = onSyncChange(() => {
      setSyncing(isSyncing());
      void recount();
    });
    void recount();
    return stop;
  }, [recount]);

  /** Loads the cached snapshot immediately, so an offline cold start still works. */
  useEffect(() => {
    if (!pharmacyId) return;
    void counterCache.read(pharmacyId).then((cached) => {
      if (cached) setSnapshotState(cached);
    });
  }, [pharmacyId]);

  const refresh = useCallback(async () => {
    if (!pharmacyId) return;
    const data = await bootstrapCounter(pharmacyId);

    /*
     * Anything still queued has NOT reached the server, so its numbers and its
     * stock are not in what just came back. `issued` is carried forward as the
     * count of unsent bills so the next offline number still clears them, and
     * their consumption stays on the local shelf.
     */
    const pending = (await outbox.pending()).filter((entry) => !entry.failed);
    const consumed: Record<string, number> = {};
    for (const entry of pending) {
      for (const line of entry.payload.lines) {
        const key = `${line.medicineId}:${line.batchId}`;
        consumed[key] = (consumed[key] ?? 0) + (line.quantity ?? 0);
      }
    }

    const next = await counterCache.refresh(pharmacyId, data, {
      issued: pending.length,
      consumed,
    });
    log.cache(
      `snapshot refreshed: series at ${data.billing.lastNumber}, ${data.shelf.length} lines, ` +
        `${pending.length} unsent carried forward`,
    );
    setSnapshotState(next);
  }, [pharmacyId]);

  const sync = useCallback(async () => {
    await drainOutbox();
    await recount();
    // Only now is the server's view complete enough to snapshot.
    await refresh().catch(() => undefined);
  }, [recount, refresh]);

  /*
   * On coming back: push first, then re-read. On first load: just read.
   */
  useEffect(() => {
    if (!pharmacyId || !online) return;
    void sync();
  }, [pharmacyId, online, sync]);

  const setSnapshot = useCallback((next: CounterSnapshot) => {
    setSnapshotState(next);
    void counterCache.write(next);
    void outbox.count().then(setQueued);
  }, []);

  /*
   * `tlpOffline()` in the console prints what the till currently believes —
   * connection, queue depth, series position, snapshot age. A support call
   * becomes one instruction rather than a tour of four screens.
   */
  useEffect(() => {
    exposeDebugHook(() => ({
      online,
      queued,
      failed,
      syncing,
      durable,
      snapshotTakenAt: snapshot?.capturedAt ?? null,
      seriesLastNumber: snapshot?.billing.lastNumber ?? null,
      issuedSinceSnapshot: snapshot?.issued ?? null,
      shelfLines: snapshot?.shelf.length ?? null,
    }));
  }, [online, queued, failed, syncing, durable, snapshot]);

  return (
    <OfflineContext
      value={{
        online,
        snapshot,
        queued,
        failed,
        syncing,
        durable,
        storageReason,
        refresh,
        sync,
        setSnapshot,
        recheckStorage,
      }}
    >
      {children}
    </OfflineContext>
  );
}

export function useOffline(): OfflineValue {
  const context = use(OfflineContext);
  if (!context) throw new Error('useOffline must be used inside <OfflineProvider>');
  return context;
}

export type { Uuid };
